import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Globe, Phone, Bot, TrendingUp, Code2, LifeBuoy, Sparkles, ArrowRight } from 'lucide-react';
import { SOLUTIONS } from '../data/solutionsData';
import './IndustryPage.css';
import './SolutionPage.css';

const ICONS = { Globe, Phone, Bot, TrendingUp, Code2, LifeBuoy };
const EASE = [0.22, 0.61, 0.36, 1];

export default function SolutionsIndex() {
  const reduce = useReducedMotion();
  const rise = (d = 0) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, amount: 0.2 },
          transition: { duration: 0.6, delay: d, ease: EASE }
        };

  return (
    <main className="ind-page">
      {/* HERO */}
      <section className="ind-hero">
        <div className="ind-wrap">
          <motion.span className="ind-eyebrow" {...rise(0)}>Your outsourced technology department</motion.span>
          <motion.h1 className="ind-h1" {...rise(0.06)}>
            Six pillars. One <span className="ind-gold">company</span>.
          </motion.h1>
          <motion.p className="ind-sub" {...rise(0.12)}>
            Versa Launch builds and manages the technology businesses rely on every day. Websites and
            commerce, phone systems, AI reception, search visibility, business software, and the
            ongoing support that keeps all of it running.
          </motion.p>
          <motion.div className="ind-cta-row" {...rise(0.18)}>
            <Link to="/plans" className="ind-btn ind-btn-primary">
              Get started <ArrowRight size={17} strokeWidth={2.4} />
            </Link>
            <Link to="/book" className="ind-btn ind-btn-ghost">See Versa in action</Link>
          </motion.div>
        </div>
      </section>

      {/* PILLAR GRID */}
      <section className="ind-section ind-section-tint">
        <div className="ind-wrap">
          <div className="solx-grid">
            {SOLUTIONS.map((sol, i) => {
              const Icon = ICONS[sol.icon] || Sparkles;
              return (
                <motion.div key={sol.slug} {...rise((i % 3) * 0.06)}>
                  <Link to={`/solutions/${sol.slug}`} className="indx-card">
                    <span className="indx-card-ic"><Icon size={24} strokeWidth={2} /></span>
                    <span className="indx-card-name">{sol.name}</span>
                    <span className="indx-card-desc">{sol.blurb}</span>
                    <span className="indx-card-go">See what we run <ArrowRight size={15} strokeWidth={2.4} /></span>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <motion.div className="solx-note" {...rise(0.1)}>
            <p className="solx-note-title">Most businesses do not need one of these. They need the set.</p>
            <p className="solx-note-text">
              A website that ranks is worth more when the phone gets answered. An AI receptionist is
              worth more when it can book into a real calendar. We run the pieces together, which is
              the part nobody selling a single service can do.
            </p>
            <Link to="/industries" className="solx-note-link">
              See how it works in your industry <ArrowRight size={15} strokeWidth={2.4} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="ind-section">
        <div className="ind-wrap">
          <motion.div className="ind-head" {...rise(0)}>
            <span className="ind-eyebrow">How an engagement runs</span>
            <h2 className="ind-h2">You hand off the technology, not the control</h2>
            <p className="ind-head-sub">
              Same team from the first call through year three. No handoff to an account manager who
              has never seen your setup.
            </p>
          </motion.div>
          <div className="ind-pains-grid">
            <motion.div className="ind-pain" {...rise(0)}>
              <span className="ind-pain-num">01</span>
              <h3 className="ind-pain-title">We audit what you already have</h3>
              <p className="ind-pain-text">
                Site, phones, listings, software, and support. We tell you what is working, what is
                costing you money, and what we would leave alone.
              </p>
            </motion.div>
            <motion.div className="ind-pain" {...rise(0.06)}>
              <span className="ind-pain-num">02</span>
              <h3 className="ind-pain-title">We build and migrate</h3>
              <p className="ind-pain-text">
                We stand up the pieces you need and move the ones you keep, in an order that never
                takes your business offline mid-season.
              </p>
            </motion.div>
            <motion.div className="ind-pain" {...rise(0.12)}>
              <span className="ind-pain-num">03</span>
              <h3 className="ind-pain-title">We run it from there</h3>
              <p className="ind-pain-text">
                Monitoring, updates, edits, and a number you can call. Your technology becomes
                something you stop thinking about.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="ind-section ind-final">
        <div className="ind-wrap">
          <motion.h2 className="ind-final-title" {...rise(0)}>Ready to put it all under one roof?</motion.h2>
          <motion.p className="ind-final-text" {...rise(0.06)}>
            Tell us what you are running today and who supports it. We will show you what one company
            behind all of it looks like, including what it costs.
          </motion.p>
          <motion.div className="ind-cta-row" {...rise(0.12)}>
            <Link to="/plans" className="ind-btn ind-btn-onfill">
              Get started <ArrowRight size={17} strokeWidth={2.4} />
            </Link>
            <Link to="/book" className="ind-btn ind-btn-onfill-ghost">See Versa in action</Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
